import { c as _c } from "react/compiler-runtime";
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
export interface KeyReadingRow {
  /** Highlighted in brass: the measurement the page is about. */
  isAccent?: boolean;
  /** Set in serif italic: a judgement rather than a number. */
  isQualitative?: boolean;
  label: string;
  /** Optional series glyph, as on the static balance chart. */
  mark?: React.ReactNode;
  value: string;
}
export interface KeyReading {
  /** A sentence under the numbers: what they mean at the key. */
  footnote?: React.ReactNode;
  keyNumber: number | null;
  /** Scientific pitch, e.g. `C4`. */
  noteName: string | null;
  rows: KeyReadingRow[];
}

/**
 * What one key reads, across the head of its chart.
 *
 * Ink-dark in both themes — it is the one panel of the interface that is: the
 * design treats it as the instrument's own display, not as a surface of the
 * page. It lies along the top of the figure rather than beside it so that the
 * plot keeps the width of the page, and it is the pointer's own echo, so it
 * never prints.
 *
 * It sticks to the top of the screen while its figure is in view: on a short
 * screen — a phone held sideways — the keyboard is at the bottom of a figure
 * taller than the screen, and a key touched there was read out of sight.
 */
export const KeyReadingStrip = t0 => {
  const $ = _c(20);
  const {
    reading
  } = t0;
  const {
    footnote,
    keyNumber,
    noteName,
    rows
  } = reading;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== keyNumber || $[1] !== t) {
    t1 = keyNumber === null ? t("Reading") : t("Reading \xB7 key #{{number}}", {
      number: keyNumber
    });
    $[0] = keyNumber;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <span className="font-mono text-[11px] tracking-[0.08em] text-ptl-reading-muted uppercase">{t1}</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = noteName ?? "\u2014";
  let t4;
  if ($[5] !== t3) {
    t4 = <span className="min-w-[2.2ch] font-serif text-[30px] leading-none">{t3}</span>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = <span className="flex items-baseline gap-3">{t2}{t4}</span>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== rows) {
    t6 = rows.map(_temp);
    $[10] = rows;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <dl className="m-0 flex flex-wrap items-baseline gap-x-6 gap-y-2">{t6}</dl>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== footnote) {
    t8 = footnote ? <p className="m-0 font-serif text-[14px] italic text-ptl-reading-muted">{footnote}</p> : null;
    $[14] = footnote;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t5 || $[17] !== t7 || $[18] !== t8) {
    t9 = <section aria-live="polite" className="sticky top-0 z-20 flex flex-wrap items-center gap-x-7 gap-y-2.5 border-b border-border bg-ptl-reading px-[18px] py-3 text-ptl-reading-foreground print:hidden">{t5}{t7}{t8}</section>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};
function _temp(row) {
  return <div key={row.label} className="flex items-baseline gap-2"><dt className="m-0 flex items-center gap-[7px] text-[12.5px] text-ptl-reading-muted">{row.mark}{row.label}</dt><dd className={cn("m-0 text-[16px] font-medium", row.isQualitative ? "font-serif italic" : "font-mono", row.isAccent ?? row.isQualitative ? "text-ptl-reading-accent" : undefined)}>{row.value}</dd></div>;
}